import React from 'react';
import styled, { css } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';

import Button from '../button';
import Chevron from '../../images/svg/chevron-icon.svg';
import parseStoryblokLink from '../../utils/parseStoryblokLink';
import mq from '../../utils/mq';

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  z-index: 3;
  padding: 140px 0 90px 0;
  ${mq.mobile(css`
    padding: 96px 0 64px 0;
  `)};
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  justify-content: center;
  ${mq.mobile(css`
    gap: 24px;
  `)};
`;

const NavigationControlsContainer = styled.div`
  display: flex;
  gap: 16px;
  ${mq.mobile(css`
    padding-top: 16px;
  `)};
`;

const Title = styled(motion.h1 as any)`
  color: #fff;
  font-size: 52px;
  font-weight: 600;
  line-height: 110%;
  letter-spacing: -1.04px;
  max-width: 536px;
  user-select: none;
  will-change: opacity, transform, filter;
  ${mq.mobile(css`
    font-size: 42px;
    max-width: unset;
  `)};
`;

const Description = styled(motion.p as any)`
  color: #fff;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.4px;
  max-width: 460px;
  user-select: none;
  will-change: opacity, transform, filter;
  ${mq.mobile(css`
    font-size: 18px;
    max-width: unset;
  `)};
`;

const CtaContainer = styled(motion.div as any)`
  width: fit-content;
  will-change: opacity, transform, filter;
`;

const Group = styled(motion.div as any)`
  display: flex;
  flex-direction: column;
  gap: 32px;
  justify-content: center;
`;

const NavigationControlButton = styled.button`
  width: 48px;
  height: 48px;
  border: 1px solid rgba(255, 255, 255, 1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  aspect-ratio: 1/1;
  background-color: transparent;
  color: rgba(255, 255, 255, 1);
  transition: all 0.15s ease-out;
  position: relative;
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    border: 1px solid rgba(255, 255, 255, 1);
    backdrop-filter: blur(10px);
    color: #000;
    transition: all 0.15s ease-out;
  }

  &:active {
    transform: scale(0.97);
  }

  svg {
    height: 20px;
  }
`;

const groupVariants = {
  hidden: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
      ease: [0.86, 0, 0.07, 1],
      type: 'linear'
    }
  },
  visible: {
    transition: {
      staggerChildren: 0.05,
      duration: 0.4,
      ease: [0.86, 0, 0.07, 1],
      type: 'tween'
    }
  },
  exit: {
    transition: {
      staggerChildren: 0.025,
      staggerDirection: 1,
      duration: 0.3,
      ease: [0.86, 0, 0.07, 1],
      type: 'linear'
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: -20, filter: 'blur(4px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, y: 30, filter: 'blur(4px)' }
};

export default function HeroCarouselTextContent({
  slides,
  activeIndex,
  onPrev,
  onNext
}: {
  slides: any[];
  activeIndex: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const activeSlide = slides[activeIndex] || {};

  return (
    <ContentContainer>
      <TextContent>
        {React.createElement(
          AnimatePresence as any,
          { exitBeforeEnter: true },
          <Group
            key={activeIndex}
            variants={groupVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Title variants={itemVariants}>{activeSlide?.titleText}</Title>
            <Description variants={itemVariants}>
              {activeSlide?.descriptionText}
            </Description>
            <CtaContainer variants={itemVariants}>
              <Button to={parseStoryblokLink(activeSlide?.ctaLink)}>
                {activeSlide?.ctaText}
              </Button>
            </CtaContainer>
          </Group>
        )}
      </TextContent>
      <NavigationControlsContainer>
        <NavigationControlButton onClick={onPrev}>
          <Chevron style={{ paddingRight: '3px' }} />
        </NavigationControlButton>
        <NavigationControlButton onClick={onNext}>
          <Chevron
            style={{ transform: 'rotate(180deg)', paddingRight: '3px' }}
          />
        </NavigationControlButton>
      </NavigationControlsContainer>
    </ContentContainer>
  );
}
